import type { MarkKind, Origin } from "./shared";

const pl = {
  badge: { AI_GENERATED: "AI", AI_EDITED: "AI edit", HUMAN: "Human" },
  title: {
    IMAGE: { AI_GENERATED: "Obraz wygenerowany przez AI", AI_EDITED: "Obraz zmodyfikowany z użyciem AI", HUMAN: "Obraz stworzony przez człowieka" },
    TEXT: { AI_GENERATED: "Treść wygenerowana przez AI", AI_EDITED: "Treść zmodyfikowana z użyciem AI", HUMAN: "Treść napisana przez człowieka" },
    ARTICLE: { AI_GENERATED: "Artykuł wygenerowany przez AI", AI_EDITED: "Artykuł zmodyfikowany z użyciem AI", HUMAN: "Artykuł napisany przez człowieka" },
  },
  textLabel: { AI_GENERATED: "Tekst wygenerowany przez AI", AI_EDITED: "Tekst zmodyfikowany z użyciem AI", HUMAN: "Tekst napisany przez człowieka" },
  pageLabel: { AI_GENERATED: "Treści na tej stronie wygenerowane przez AI", AI_EDITED: "Treści na tej stronie zmodyfikowane z użyciem AI", HUMAN: "Treści napisane przez człowieka" },
  soft: {
    text: "Przygotowano z pomocą AI",
    article: "Artykuł przygotowany z pomocą AI",
    page: "Treści przygotowane z pomocą AI",
    verified: (who: string) => `zweryfikowane przez ${who}`,
  },
  details: "Pokaż szczegóły oznaczenia",
  euNote: "Oznaczenie ikoną UE zgodnie z Kodeksem postępowania (AI Act, art. 50)",
  tool: "Narzędzie",
  publisher: "Opublikowane przez",
  marked: "Oznaczono",
  reviewed: "Zweryfikowane przez redakcję",
  more: "Przejrzystość AI na tej stronie",
  legal: "Informacja zgodnie z art. 50 AI Act",
  close: "Zamknij",
  edit: {
    mode: "Tryb oznaczania",
    hint: "Kliknij akapit, zdjęcie lub tło · Shift+klik: tło pod tekstem · Alt+klik: otwórz link",
    hintBrowse: "Klikaj linki i przechodź między podstronami",
    browsing: "Przeglądanie",
    browse: "Przeglądaj stronę",
    label: "Wróć do oznaczania",
    exit: "Zakończ",
    origin: "Pochodzenie",
    tool: "Narzędzie / model",
    toolPh: "np. Midjourney, ChatGPT",
    reviewed: "Zatwierdzone przez redakcję",
    scope: "Zakres",
    scopePage: "Tylko ta podstrona",
    scopeSite: "Cała witryna (to samo zdjęcie wszędzie)",
    scopeSiteArticle: "Cała witryna (wszystkie podstrony)",
    article: "Oznacz cały artykuł",
    articleHint: "Jedna etykieta nad tytułem obejmie całą tę podstronę.",
    save: "Zapisz oznaczenie",
    remove: "Usuń oznaczenie",
    cancel: "Anuluj",
    saved: "Zapisano",
    removed: "Usunięto",
    error: "Błąd zapisu. Spróbuj ponownie.",
    limit: "Limit oznaczeń w Twoim planie został wykorzystany. Zmień plan w panelu AIMARKER (zakładka Plan).",
    invalid: "Link trybu oznaczania wygasł. Wygeneruj nowy w panelu AIMARKER.",
    count: (n: number) => {
      const w = n === 1 ? "oznaczenie" : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? "oznaczenia" : "oznaczeń";
      return `${n} ${w} na tej podstronie`;
    },
    as: "Zalogowano jako",
    assess: {
      title: "Czy to trzeba oznaczyć? (AI Act art. 50)",
      pick: "—",
      yes: "Tak",
      no: "Nie",
      unsure: "Nie wiem",
      aiShare: "Udział AI w tekście",
      generated: "Wygenerowało",
      substantial: "Istotnie zmieniło",
      minor: "Tylko korekta / styl",
      publicInterest: "Informuje o sprawach interesu publicznego (zdrowie, prawo, finanse, wiadomości)?",
      humanReview: "Przegląd człowieka przed publikacją?",
      editorial: "Osoba lub firma odpowiedzialna redakcyjnie?",
      realistic: "Realistyczne, może uchodzić za prawdziwe?",
      people: "Przedstawia realistyczne osoby?",
      artistic: "Oczywiście artystyczne / satyryczne?",
      clinical: "Pacjenci, personel, zabiegi lub efekty leczenia?",
      note: "Pomoc w decyzji, nie porada prawna. Ocena zapisze się przy oznaczeniu.",
    },
  },
};

type Dict = typeof pl;

const en: Dict = {
  badge: { AI_GENERATED: "AI", AI_EDITED: "AI edit", HUMAN: "Human" },
  title: {
    IMAGE: { AI_GENERATED: "AI-generated image", AI_EDITED: "AI-modified image", HUMAN: "Human-made image" },
    TEXT: { AI_GENERATED: "AI-generated content", AI_EDITED: "AI-modified content", HUMAN: "Human-written content" },
    ARTICLE: { AI_GENERATED: "AI-generated article", AI_EDITED: "AI-modified article", HUMAN: "Human-written article" },
  },
  textLabel: { AI_GENERATED: "AI-generated text", AI_EDITED: "AI-modified text", HUMAN: "Human-written text" },
  pageLabel: { AI_GENERATED: "Content on this page is AI-generated", AI_EDITED: "Content on this page is AI-modified", HUMAN: "Human-written content" },
  soft: {
    text: "Prepared with AI",
    article: "Article prepared with AI",
    page: "Content prepared with AI",
    verified: (who: string) => `verified by ${who}`,
  },
  details: "Show label details",
  euNote: "Labelled with the EU icon under the Code of Practice (AI Act, Art. 50)",
  tool: "Tool",
  publisher: "Published by",
  marked: "Labelled",
  reviewed: "Reviewed by editorial team",
  more: "AI transparency on this site",
  legal: "Disclosure under Art. 50 of the EU AI Act",
  close: "Close",
  edit: {
    mode: "Labelling mode",
    hint: "Click a paragraph, image or background · Shift+click: background under text · Alt+click: follow link",
    hintBrowse: "Click links to move between pages",
    browsing: "Browsing",
    browse: "Browse site",
    label: "Back to labelling",
    exit: "Exit",
    origin: "Origin",
    tool: "Tool / model",
    toolPh: "e.g. Midjourney, ChatGPT",
    reviewed: "Approved by an editor",
    scope: "Scope",
    scopePage: "This page only",
    scopeSite: "Whole site (same image everywhere)",
    scopeSiteArticle: "Whole site (all pages)",
    article: "Label whole article",
    articleHint: "One label above the headline covers this whole page.",
    save: "Save label",
    remove: "Remove label",
    cancel: "Cancel",
    saved: "Saved",
    removed: "Removed",
    error: "Could not save. Please try again.",
    limit: "Your plan's label limit is used up. Upgrade in the AIMARKER dashboard (Plan tab).",
    invalid: "This labelling link has expired. Create a new one in the AIMARKER dashboard.",
    count: (n: number) => `${n} ${n === 1 ? "label" : "labels"} on this page`,
    as: "Signed in as",
    assess: {
      title: "Does this need a label? (AI Act art. 50)",
      pick: "—",
      yes: "Yes",
      no: "No",
      unsure: "Not sure",
      aiShare: "AI contribution to the text",
      generated: "Generated it",
      substantial: "Substantially edited",
      minor: "Proofreading / style only",
      publicInterest: "Informs on matters of public interest (health, law, finance, news)?",
      humanReview: "Human review before publication?",
      editorial: "A person or company holds editorial responsibility?",
      realistic: "Realistic, could pass as real?",
      people: "Shows realistic people?",
      artistic: "Evidently artistic / satirical?",
      clinical: "Patients, staff, treatments or treatment results?",
      note: "Decision support, not legal advice. The assessment is stored with the label.",
    },
  },
};

export type Lang = "pl" | "en";
export const dict = (lang: Lang): Dict => (lang === "pl" ? pl : en);

/**
 * Page language: explicit policy → <html lang> when it says Polish → the text itself.
 * Many CMS themes ship with lang="en-US" on Polish sites, so a Polish-looking text wins over an English lang attribute.
 */
export function detectLang(pref: "auto" | Lang): Lang {
  if (pref !== "auto") return pref;
  const declared = (document.documentElement.lang || "").toLowerCase();
  if (declared.startsWith("pl")) return "pl";
  if (looksPolish()) return "pl";
  if (declared) return "en";
  return (navigator.language || "en").toLowerCase().startsWith("pl") ? "pl" : "en";
}

function looksPolish(): boolean {
  const text = (document.body?.innerText || document.title || "").slice(0, 6000);
  const letters = text.match(/\p{L}/gu)?.length ?? 0;
  if (letters < 80) return false;
  const pl = text.match(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g)?.length ?? 0;
  return pl / letters > 0.006;
}

export function titleFor(d: Dict, kind: MarkKind, origin: Origin) {
  return d.title[kind][origin];
}
